import help from "assets/icons/help.svg";
import ModalWindow from "hooks/ModalWindow/ModalWindow";
import PinCode from "hooks/PinCode/PinCode";
import ButtonLoader from "hooks/UseLoading/ButtonLoader";
import useLoading from "hooks/UseLoading/UseLoading";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import eye from "assets/icons/eye.png";
import hidden from "assets/icons/hidden.png";
import {
  ChangePin,
  SendDevice,
  getUserInfo,
  login,
  postToMail,
} from "service/AuthService";
import {
  checkPinCode,
} from "service/AuthService";

import { changePinCode, postMail, setUser } from "store/slices/UserSlice";
import { setPinVerified } from "store/slices/AuthSlice";
import { formatLockedMessage } from "utils/roleRedirect";
import Notification from "utils/Notifications";
import tokenDecode from "utils/decode";
import { ScaleLoader } from "react-spinners";
import { Button } from "components";
import RegisterForTest from "../RegisterForm/RegisterForTest";
import RecoverPasswordForm from "../RecoverPasswordForm/RecoverPasswordForm";
import RecoverPinForm from "../RecoverPinForm/RecoverPinForm";

import styles from "./LoginForm.module.scss";


function LoginForm() {
  const { t } = useTranslation();
  const [loader, setLoader] = useState(false);
  const [loginMail, setLoginMail] = useState(false);
  const [recoverPassword, setRecoverPassword] = useState(false);
  const [recoverPin, setRecoverPin] = useState(false);

  const [password, setPassword] = useState("");
  const [userPinCode, setUserPinCode] = useState();
  const [pinCode, setPinCode] = useState();
  const [userId, setUserId] = useState();
  const [email, setEmail] = useState("");
  const [emailCor, setEmailCor] = useState("");
  const [device, setDevice] = useState("");

  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    setDevice(userAgent);
  }, []);

  // FIX (Промежуток): поддержка deep-link /recover-password/ и /recover-pin/.
  // Также обрабатываем ?recover=password|pin для гибкости.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = window.location.pathname || "";
    const params = new URLSearchParams(window.location.search);
    const action = params.get("recover");
    if (path.startsWith("/recover-password") || action === "password") {
      setRecoverPassword(true);
    } else if (path.startsWith("/recover-pin") || action === "pin") {
      setRecoverPin(true);
    }
  }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState(false);
  const [datainfo, setDataInfo] = useState(false);

  // 2FA TOTP-flow
  const [totpRequired, setTotpRequired] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  let socket = null;

  const sendMessage = (message) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(message);
    }
  };

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const user = useSelector((state) => state.user);

  const { loading, withLoading } = useLoading(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (email === "") {
      setNotify({
        isOpen: true,
        message: t("auth.enter_inn", "Введите ИНН!"),
        type: "warning",
        sound: "warning",
      });
    } else if (password === "") {
      setNotify({
        isOpen: true,
        message: t("auth.enter_password", "Введите пароль!"),
        type: "warning",
        sound: "warning",
      });
    } else {
      await doLogin();
    }
  };

  const doLogin = async (codeOverride) => {
    const code = codeOverride !== undefined ? codeOverride : (totpRequired ? totpCode : "");
    try {
      await withLoading(async () => {
        let response = await login(email, password, code);
        localStorage.setItem("token", response.data.access);
        if (response.data.refresh) {
          localStorage.setItem("refresh", response.data.refresh);
        }
        if (response.data.roles) {
          localStorage.setItem("login_roles", JSON.stringify(response.data.roles));
        }
        if (response.data.primary_role) {
          localStorage.setItem("login_primary_role", response.data.primary_role);
        }
        const userId = tokenDecode(response.data.refresh);
        if (userId == null) {
          throw new Error(t("auth.bad_token", "Не удалось получить идентификатор пользователя из токена."));
        }

        let userResponse = await getUserInfo(userId);
        const data = { ...(userResponse.data || {}), token: response.data.access };

        setTotpRequired(false);
        setTotpCode("");
        setUserPinCode(data.pin);
        setDataInfo(data);
        setOpenModal(true);
        setUserId(userId);
      });
    } catch (error) {
      const errCode = error?.response?.data?.code;
      const detail = error?.response?.data?.detail;

      // Бэк сообщил что нужен TOTP-код — показываем поле и ждём ввод
      if (errCode === "totp_required" || error?.response?.data?.totp_required === true) {
        setTotpRequired(true);
        setNotify({
          isOpen: true,
          message: t("auth.enter_totp", "Введите 6-значный код из приложения-аутентификатора"),
          type: "info",
          sound: "warning",
        });
        return;
      }

      // Неверный TOTP — очистим поле и попросим заново
      if (errCode === "totp_invalid") {
        setTotpCode("");
        setNotify({
          isOpen: true,
          message: t("auth.totp_invalid", "Неверный код TOTP. Попробуйте ещё раз."),
          type: "error",
          sound: "error",
        });
        return;
      }

      if (error?.response?.status === 423) {
        const until = error.response?.data?.locked_until;
        setNotify({
          isOpen: true,
          message: formatLockedMessage(until),
          type: "error",
          sound: "error",
        });
      } else {
        setNotify({
          isOpen: true,
          message:
            detail ||
            (error?.response?.data?.password
              ? `${t("auth.password", "Пароль")} ${error.response.data.password}`
              : t("auth.invalid_credentials", "Неверные ИНН или пароль")),
          type: "error",
          sound: "error",
        });
      }
    }
  };

  const handleTotpSubmit = async (event) => {
    event.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      setNotify({
        isOpen: true,
        message: t("auth.enter_6_code", "Введите 6-значный код"),
        type: "warning",
        sound: "warning",
      });
      return;
    }
    await doLogin(totpCode);
  };

  const pinLogIn = async () => {
    localStorage.setItem("mute", "false");

    if (String(pinCode).length === 4) {
      setLoader(true);

      try {
        const response = await checkPinCode(pinCode, userId);

        if (response.data.success) {
          if (response.data.access) {
            localStorage.setItem("token", response.data.access);
          }
          if (response.data.refresh) {
            localStorage.setItem("refresh", response.data.refresh);
          }
          dispatch(setPinVerified(true));

          setNotify({
            isOpen: true,
            message: t("auth.welcome", "Добро пожаловать!"),
            type: "success",
            sound: "enter",
          });

          try {
            const socket = new WebSocket("wss://sys.chu.kg/ws/online_status/");
            socket.onopen = () => {
              socket.send(JSON.stringify({ command: "connect", user_id: userId }));
              socket.close();
            };
          } catch (e) {  }

          try { await SendDevice(device); } catch (e) {}

          setUserId(datainfo.user);
          sendMessage({ user_id: userId });
          dispatch(setUser(datainfo));

          setTimeout(() => { window.location.href = "/post-login/"; }, 600);

        } else {
          setNotify({
            isOpen: true,
            message: response.data.remaining !== undefined
              ? `${t("auth.wrong_pin", "Неправильный ПИН")}. ${t("auth.attempts_left", "Осталось попыток")}: ${response.data.remaining}`
              : t("auth.wrong_pin", "Неправильный Пин-Код"),
            type: "error",
            sound: "error",
          });
        }
      } catch (error) {
        if (error?.response?.status === 423) {
          const until = error.response?.data?.locked_until;
          setNotify({
            isOpen: true,
            message: formatLockedMessage(until),
            type: "error",
            sound: "error",
          });
        } else {
          setNotify({
            isOpen: true,
            message: error?.response?.data?.error || t("common.server_error", "Ошибка сервера"),
            type: "error",
            sound: "error",
          });
        }
      } finally {
        setLoader(false);
      }

    } else {
      setNotify({
        isOpen: true,
        message: t("auth.enter_4_pin", "Введите 4-значный ПИН-код"),
        type: "warning",
        sound: "warning",
      });
    }
  };

  const createUserPin = async () => {
    if (String(pinCode)?.length < 4 || !pinCode) {
      setNotify({
        isOpen: true,
        message: t("auth.enter_pin", "Введите Пин!"),
        type: "error",
        sound: "error",
      });
    } else if (String(pinCode).length === 4) {
      try {
        setLoader(true);

        await ChangePin(pinCode);
        dispatch(
          changePinCode({
            pin: pinCode,
          })
        );
        setTimeout(() => {
          setNotify({
            isOpen: true,
            message: t("auth.pin_set", "Пин успешно задан"),
            type: "success",
            sound: "enter"
          });
        }, 1000);

        setNotify({
          isOpen: true,
          message: t("auth.welcome", "Добро пожаловать!"),
          type: "success",
          sound: "enter"
        });

        try { await SendDevice(device); } catch (e) {}

        setUserId(datainfo.user);
        sendMessage({ user_id: userId });
        dispatch(setUser(datainfo));

        setTimeout(() => {
          navigate("/post-login/");
        }, 1500);
      } catch (error) {
        setNotify({
          isOpen: true,
          message: t("common.error", "Ошибка"),
          type: "error",
          sound: "error",
        });
      }
    }
  };

  const handleSubmitByMail = async (event) => {
    event.preventDefault();

    await postToMail(emailCor);
    dispatch(
      postMail({
        email: emailCor,
      })
    );

    setNotify({
      isOpen: true,
      message: t("auth.mail_sent", "Сообщение на почту успешно отправлено!"),
      type: "success",
      sound: "success",
    });
  };

  const RecoverPin = () => {
    setOpenModal(false)
    setLoginMail(false)
    setRecoverPassword(false)
    setRecoverPin(true)
  };

  useEffect(() => {
    localStorage.setItem("user", JSON.stringify(user));
  }, [user]);

  return (
    <div>
      {loginMail === false ? (
        recoverPassword === false ? (
          recoverPin === false ? (
            <div className="unv-form-card">
              <h2 className={`${styles.login__title} unv-form-card__title`}>
                {t("auth.welcome_chu", "Рады видеть вас в CHU!")}
              </h2>
              <h4 className={`${styles.login__untitle} unv-form-card__subtitle`}>
                {t("auth.signin_subtitle", "Пожалуйста, войдите в свою учетную запись")}
              </h4>
              {!totpRequired ? (
                <form onSubmit={handleSubmit} noValidate>
                  <label className={styles.login__title__label} htmlFor="login">
                    {t("auth.inn", "ИНН")}
                  </label>
                  <input
                    id="login"
                    name="username"
                    value={email}
                    required
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="username"
                    aria-invalid={notify.type === "error" || undefined}
                    onChange={(e) => setEmail(e.target.value.replace(/\D/g, ""))}
                    className={`${styles.login__input} unv-input`}
                  />

                  <div>
                    <label className={styles.login__title__label} htmlFor="password">
                      {t("auth.password", "Пароль")}
                    </label>
                    <div className={styles.input__wrap}>
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={password}
                        required
                        autoComplete="current-password"
                        aria-invalid={notify.type === "error" || undefined}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`${styles.login__input} ${styles.input__withIcon} unv-input`}
                      />
                      <button
                        type="button"
                        className={styles.show__password}
                        onClick={togglePasswordVisibility}
                        aria-label={
                          showPassword
                            ? t("auth.hide_password", "Скрыть пароль")
                            : t("auth.show_password", "Показать пароль")
                        }
                      >
                        <img src={showPassword ? hidden : eye} alt="" />
                      </button>
                    </div>
                  </div>
                  <div className={styles.button__wrapper}>
                    <ButtonLoader loading={loading} position={"center"}>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="auth__btn"
                        style={{ margin: "15px 0 0px 0" }}
                      >
                        {t("auth.signin", "Войти")}
                      </Button>
                    </ButtonLoader>
                    <button
                      type="button"
                      className={styles.register_button_reset}
                      onClick={() => setRecoverPassword(true)}
                    >
                      {t("auth.forgot_password", "Забыли пароль?")}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleTotpSubmit} noValidate>
                  <h4 className={styles.login__untitle}>
                    {t("auth.totp_prompt", "Введите 6-значный код из приложения-аутентификатора (Google Authenticator / Authy)")}
                  </h4>
                  <label className={styles.login__title__label} htmlFor="totp_code">
                    {t("auth.code_from_app", "Код из приложения")}
                  </label>
                  <input
                    id="totp_code"
                    name="totp_code"
                    value={totpCode}
                    required
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className={`${styles.login__input} unv-input`}
                  />
                  <div className={styles.button__wrapper}>
                    <ButtonLoader loading={loading} position={"center"}>
                      <Button
                        type="submit"
                        disabled={loading || totpCode.length !== 6}
                        className="auth__btn"
                        style={{ margin: "15px 0 0px 0" }}
                      >
                        {t("auth.verify", "Подтвердить")}
                      </Button>
                    </ButtonLoader>
                    <button
                      type="button"
                      className={styles.register_button_reset}
                      onClick={() => { setTotpRequired(false); setTotpCode(""); setPassword(""); }}
                    >
                      {t("common.cancel", "Отмена")}
                    </button>
                  </div>
                </form>
              )}

              <Notification notify={notify} setNotify={setNotify} />
            </div>
          ) : (
            <div style={{ maxWidth: "470px" }}>
              <RecoverPinForm setRecoverPin={setRecoverPin} />
            </div>
          )
        ) : (
          <div style={{ maxWidth: "470px" }}>
            <RecoverPasswordForm setRecoverPassword={setRecoverPassword} RecoverPin={RecoverPin} />
          </div>
        )
      ) : (
        <div style={{ maxWidth: "470px" }}>
          <RegisterForTest setLoginMail={setLoginMail} RecoverPin={RecoverPin} />
        </div>
      )}
      {!userPinCode ? (
        <ModalWindow
          openModal={openModal}
          setOpenModal={setOpenModal}
          modalTitle={
            <p>{t("auth.welcome", "Добро пожаловать!")}</p>
          }
          modalText={t("auth.create_pin", "Придумайте ПИН-код")}
          width={"unset"}
        >
          <PinCode setPinCode={setPinCode} passwordVisible={false} />

          {loader ? (
            <Button
              type="button"
              style={{ width: "unset", border: "none", background: "none" }}
            >
              <ScaleLoader color="black" size={15} />
            </Button>
          ) : (
            <>
              <Button
                type="button"
                onClick={() => setOpenModal(false)}
                className={styles.btn_pin_close}
              >
                {t("common.close", "Закрыть")}
              </Button>
              <Button
                type="button"
                style={{ width: "unset" }}
                onClick={createUserPin}
                disabled={loader}
                className={styles.btn_pin}
              >
                {t("common.save", "Сохранить")}
              </Button>
            </>
          )}
        </ModalWindow>
      ) : (
        <ModalWindow
          openModal={openModal}
          setOpenModal={setOpenModal}
          modalText={t("auth.enter_pin", "Введите ПИН-код")}
          width={"unset"}
        >
          <>
            <PinCode setPinCode={setPinCode} passwordVisible={false} />
            <Button
              type="button"
              onClick={() => setOpenModal(false)}
              className={styles.btn_pin_close}
            >
              {t("common.close", "Закрыть")}
            </Button>
            <Button type="button" onClick={pinLogIn} disabled={loader} className={styles.btn_pin}>
              {t("auth.signin", "Войти")}
            </Button>
          </>
          <div className={styles.div_register_button} style={{ marginTop: '5px' }}>
            <button
              type="button"
              className={styles.register_button_reset}
              onClick={() => RecoverPin()}
              style={{ margin: "0px 10px 0px 0" }}
            >
              {t("auth.forgot_pin", "Забыли пин?")}
            </button>
          </div>
        </ModalWindow>
      )}
    </div>
  );
}

export default LoginForm;
